import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowLeft, Download, Upload, Trash2, Lock, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { since } from "@/lib/lastSeen";

const IST_OFFSET_MIN = 330;

function fmtTime(iso, isRegularized) {
  if (!iso) return "—";
  const m = String(iso).match(/([+-])(\d{2}):(\d{2})$/);
  const isUtc = /Z$/.test(iso) || (m && m[2] === "00" && m[3] === "00");
  if (!isUtc || isRegularized) {
    const t = String(iso).slice(11, 16);
    return t || "—";
  }
  const d = new Date(iso);
  const shifted = new Date(d.getTime() + IST_OFFSET_MIN * 60000);
  return shifted.toISOString().slice(11, 16);
}

function initials(name = "") {
  return name.split(" ").map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
}

function fmtDate(d) {
  if (!d) return "—";
  try {
    return new Date(d + "T00:00:00").toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return d; }
}

function Field({ label, value }) {
  return (
    <div className="py-2 border-b border-zinc-100 last:border-0">
      <div className="text-xs text-zinc-500">{label}</div>
      <div className="text-sm text-zinc-900 mt-0.5 break-words">{value || "—"}</div>
    </div>
  );
}

const DOC_CATEGORIES = [
  ["offer_letter", "Offer letter"], ["hike_letter", "Hike letter"], ["payslip", "Payslip"],
  ["contract", "Contract"], ["id_proof", "ID proof"], ["certificate", "Certificate"], ["other", "Other"],
];

function fmtSize(b) {
  if (!b) return "—";
  return b < 1024 * 1024 ? `${Math.round(b / 1024)} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`;
}

export function DocumentsTab({ asEmployee, employeeId, isAdmin, canUpload = isAdmin }) {
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);

  const [category, setCategory] = useState("offer_letter");
  const [isPrivate, setIsPrivate] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/employees/${employeeId}/documents`, { params: asEmployee ? { as_employee: true } : {} }); setDocs(data || []); }
    catch (e) { /* access handled by server */ }
  };
  useEffect(() => { load(); }, [employeeId]);

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", category);
    fd.append("is_private", isPrivate ? "true" : "false");
    setBusy(true);
    try {
      await api.post(`/employees/${employeeId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const remove = async (d) => {
    try { await api.delete(`/employees/${employeeId}/documents/${d.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const download = (d) => {
    window.open(`/api/employees/${employeeId}/documents/${d.id}/download`, "_blank");
  };

  return (
    <div className="space-y-4">
      {canUpload && (
        <Card className="border-zinc-200 shadow-none"><CardContent className="p-4">
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <div className="text-xs text-zinc-500 mb-1">Category</div>
              <select value={category} onChange={(e) => setCategory(e.target.value)}
                className="h-9 rounded-md border border-zinc-300 px-2 text-sm bg-white">
                {DOC_CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            {isAdmin && <label className="flex items-center gap-2 text-sm text-zinc-700 h-9">
              <input type="checkbox" checked={isPrivate} onChange={(e) => setIsPrivate(e.target.checked)} />
              Internal only
            </label>}
            <label className="ml-auto">
              <input type="file" className="hidden" disabled={busy}
                onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }}
                data-testid="doc-upload-input" />
              <span className="inline-flex items-center gap-1 h-9 px-3 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm cursor-pointer">
                <Upload className="h-4 w-4" /> {busy ? "Uploading…" : "Upload file"}
              </span>
            </label>
          </div>
          <p className="mt-2 text-[11px] text-zinc-500">
            PDF, Word, Excel, CSV or images · up to 10 MB.{isAdmin ? " Internal-only files are never shown to the employee." : ""}
          </p>
        </CardContent></Card>
      )}

      {docs.length ? (
        <div className="flex justify-end pb-3">
          <Button variant="outline" size="sm" data-testid="docs-all"
                  onClick={() => window.open(`/api/employees/${employeeId}/documents/download-all` + (asEmployee ? "?as_employee=true" : ""), "_blank")}>
            Download all documents
          </Button>
        </div>
      ) : null}
      <Card className="border-zinc-200 shadow-none"><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow className="hover:bg-transparent">
            <TableHead>File</TableHead><TableHead>Category</TableHead>
            <TableHead>Size</TableHead><TableHead>Uploaded</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {docs.map((d) => (
              <TableRow key={d.id} className="border-b border-zinc-100">
                <TableCell className="text-sm">
                  <span className="text-zinc-900">{d.filename}</span>
                  {d.is_private && <Badge variant="outline" className="ml-2 text-[10px] border-amber-300 text-amber-700"><Lock className="h-3 w-3 mr-0.5" />Internal</Badge>}
                </TableCell>
                <TableCell className="text-sm text-zinc-600">
                  {(DOC_CATEGORIES.find(([v]) => v === d.category) || [, d.category])[1]}
                </TableCell>
                <TableCell className="text-sm tabular-nums">{fmtSize(d.size)}</TableCell>
                <TableCell className="text-sm text-zinc-500">{(d.uploaded_at || "").slice(0, 10)}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" className="h-8" onClick={() => download(d)}><Download className="h-4 w-4" /></Button>
                  {isAdmin && <Button size="sm" variant="ghost" className="h-8 text-red-600" onClick={() => remove(d)}><Trash2 className="h-4 w-4" /></Button>}
                </TableCell>
              </TableRow>
            ))}
            {!docs.length && <TableRow><TableCell colSpan={5} className="text-center text-sm text-zinc-500 py-10">No documents yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}

const EVENT_TYPES = [
  ["joined", "Joined"], ["converted", "Converted to full-time"],
  ["internship_completed", "Internship completed"], ["hike", "Salary hike"],
  ["promotion", "Promotion"], ["exit", "Exit"], ["rejoined", "Rejoined"],
];
const DOT = {
  joined: "bg-emerald-500", rejoined: "bg-emerald-500", converted: "bg-emerald-600",
  hike: "bg-blue-500", promotion: "bg-violet-500",
  internship_completed: "bg-zinc-400", exit: "bg-amber-500",
  probation_started: "bg-zinc-300", probation_completed: "bg-emerald-400",
  probation_due: "bg-amber-400",
};

export function TimelineTab({ employeeId, isAdmin }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: "", type: "hike", designation: "", detail: "", note: "" });
  const [busy, setBusy] = useState(false);

  const extendProbation = async () => {
    const cur = data?.probation;
    if (!cur) return;
    const d = window.prompt(`New probation end date (YYYY-MM-DD).\nCurrent end: ${cur.end}`, cur.end);
    if (!d) return;
    const reason = window.prompt("Why is probation being extended? This is recorded on the timeline.");
    if (!reason) return;
    try {
      const { data: r } = await api.post(`/employees/${employeeId}/probation/extend`,
        { new_end_date: d, reason, window_start: cur.start });
      toast.success(`Probation extended from ${r.was} to ${r.now}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not extend probation");
    }
  };

  const load = async () => {
    try { const { data } = await api.get(`/employees/${employeeId}/timeline`); setData(data); }
    catch (e) { /* access handled server-side */ }
  };
  useEffect(() => { load(); }, [employeeId]);

  const save = async () => {
    if (!form.date) { toast.error("Pick a date"); return; }
    setBusy(true);
    try {
      await api.post(`/employees/${employeeId}/timeline`, form);
      toast.success("Milestone added");
      setForm({ date: "", type: "hike", designation: "", detail: "", note: "" });
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const remove = async (ev) => {
    try { await api.delete(`/employees/${employeeId}/timeline/${ev.id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!data) return <p className="text-sm text-zinc-500">Loading…</p>;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="border-zinc-200 shadow-none"><CardContent className="p-4">
          <div className="text-xs text-zinc-500">Total tenure</div>
          <div className="text-2xl font-semibold mt-1">{data.total_label}</div>
          {data.has_break && <div className="text-[11px] text-amber-700 mt-1">excludes break · {data.elapsed_label} elapsed</div>}
        </CardContent></Card>
        <Card className="border-zinc-200 shadow-none"><CardContent className="p-4">
          <div className="text-xs text-zinc-500">Current role</div>
          <div className="text-sm font-medium mt-1">{data.current_designation || "—"}</div>
          <div className="text-[11px] text-zinc-500 mt-1">{data.active ? "Active" : "Not active"}</div>
        </CardContent></Card>
        <Card className="border-zinc-200 shadow-none"><CardContent className="p-4">
          <div className="text-xs text-zinc-500">Stints</div>
          <div className="text-2xl font-semibold mt-1">{data.phases.length}</div>
        </CardContent></Card>
        <Card className="border-zinc-200 shadow-none"><CardContent className="p-4">
          <div className="text-xs text-zinc-500">Milestones</div>
          <div className="text-2xl font-semibold mt-1">{data.events.length}</div>
        </CardContent></Card>
      </div>

      {data.probation && data.probation.state !== "unknown" && (
        <Card className="border-zinc-200 shadow-none"><CardContent className="p-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-xs text-zinc-500">
                Probation · {data.probation.kind === "intern" ? "intern" : "employee"} · {data.probation.months} month{data.probation.months === 1 ? "" : "s"}
              </div>
              <div className="text-sm font-medium text-zinc-900 mt-1" data-testid="probation-status">
                {data.probation.state === "completed"
                  ? `Completed ${fmtDate(data.probation.end)}`
                  : data.probation.state === "ended_early"
                  ? `Ended early ${fmtDate(data.probation.end)}`
                  : `Completes ${fmtDate(data.probation.end)} · ${data.probation.days_left} day${data.probation.days_left === 1 ? "" : "s"} to go`}
                {data.probation.extended ? " · extended" : ""}
              </div>
            </div>
            {isAdmin && data.probation.state === "in_progress" && (
              <Button size="sm" variant="outline" className="h-8" onClick={extendProbation} data-testid="extend-probation-btn">Extend</Button>
            )}
          </div>
        </CardContent></Card>
      )}

      {data.phases.length > 1 && (
        <Card className="border-zinc-200 shadow-none"><CardContent className="p-4">
          <div className="text-xs text-zinc-500 mb-2">Tenure by stint</div>
          {data.phases.map((p, i) => (
            <div key={i} className="flex items-center justify-between text-sm py-1 border-b border-zinc-100 last:border-0">
              <span>{p.label || "—"} <span className="text-zinc-500">· {fmtDate(p.from)} – {p.to ? fmtDate(p.to) : "present"}</span></span>
              <span className="tabular-nums text-zinc-700">{p.months} months</span>
            </div>
          ))}
        </CardContent></Card>
      )}

      {isAdmin && (
        <div>
          {!open ? (
            <Button onClick={() => setOpen(true)} className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="add-milestone">
              <Plus className="h-4 w-4 mr-1" /> Add milestone
            </Button>
          ) : (
            <Card className="border-zinc-200 shadow-none"><CardContent className="p-4 space-y-3">
              <div className="grid md:grid-cols-2 gap-3">
                <div><div className="text-xs text-zinc-500 mb-1">Date</div>
                  <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                    className="h-9 w-full rounded-md border border-zinc-300 px-2 text-sm" data-testid="milestone-date" /></div>
                <div><div className="text-xs text-zinc-500 mb-1">Event</div>
                  <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
                    className="h-9 w-full rounded-md border border-zinc-300 px-2 text-sm bg-white">
                    {EVENT_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select></div>
                <div><div className="text-xs text-zinc-500 mb-1">Designation</div>
                  <input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })}
                    placeholder="Senior Recruiter" className="h-9 w-full rounded-md border border-zinc-300 px-2 text-sm" /></div>
                <div><div className="text-xs text-zinc-500 mb-1">Detail</div>
                  <input value={form.detail} onChange={(e) => setForm({ ...form, detail: e.target.value })}
                    placeholder="12% or 45,000 to 52,000" className="h-9 w-full rounded-md border border-zinc-300 px-2 text-sm" /></div>
              </div>
              <div className="flex gap-2">
                <Button onClick={save} disabled={busy} className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white">{busy ? "Saving…" : "Save"}</Button>
                <Button variant="outline" onClick={() => setOpen(false)} className="h-9">Cancel</Button>
              </div>
            </CardContent></Card>
          )}
        </div>
      )}

      <Card className="border-zinc-200 shadow-none"><CardContent className="p-5">
        {!data.events.length ? <p className="text-sm text-zinc-500">No milestones yet.</p> : (
          <div className="relative pl-6 border-l-2 border-zinc-200">
            {data.events.map((ev) => (
              <div key={ev.id} className="relative pb-5 last:pb-0 group">
                <span className={`absolute -left-[31px] top-1 h-2.5 w-2.5 rounded-full ${DOT[ev.type] || "bg-zinc-400"}`} />
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-medium text-zinc-900">{ev.label}</div>
                    <div className="text-[11px] text-zinc-500">
                      {fmtDate(ev.date)}{ev.designation ? ` · ${ev.designation}` : ""}{ev.detail ? ` · ${ev.detail}` : ""}
                    </div>
                  </div>
                  {isAdmin && !ev.derived && (
                    <Button size="sm" variant="ghost" className="h-7 text-red-600 opacity-0 group-hover:opacity-100"
                      onClick={() => remove(ev)}><Trash2 className="h-3.5 w-3.5" /></Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent></Card>
    </div>
  );
}

// The employee's own profile page, read-only, for an admin looking at
// someone else. It reuses DocumentsTab and TimelineTab so this cannot
// drift away from what the employee actually sees.
// The payslips the employee can actually see. Mirrors their own page:
// only released months, and the payable figure including reimbursements.
function SelfPayslips({ employeeId }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    let alive = true;
    api.get("/payslips", { params: { employee_id: employeeId, as_employee: true } })
      .then((r) => { if (alive) setRows(r.data || []); })
      .catch((e) => { if (alive) setErr(formatApiError(e)); });
    return () => { alive = false; };
  }, [employeeId]);
  const inr = (n) => "₹ " + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
  if (err) return <div className="text-sm text-red-600">{err}</div>;
  if (!rows) return <div className="text-sm text-zinc-500">Loading</div>;
  return (
    <Card className="border-zinc-200 shadow-none">
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Month</TableHead>
              <TableHead className="text-right">Payable</TableHead>
              <TableHead className="text-right">PDF</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((p) => (
              <TableRow key={p.id} data-testid={"self-payslip-" + p.id}>
                <TableCell className="text-sm">{p.month}</TableCell>
                <TableCell className="text-sm text-right tabular-nums font-semibold text-emerald-700">
                  {inr(p.total_payment != null ? p.total_payment : p.net_pay)}
                  {p.reimbursements ? (
                    <div className="text-[11px] font-normal text-zinc-500">
                      net {inr(p.net_pay)} plus {inr(p.reimbursements)} reimbursed
                    </div>
                  ) : null}
                </TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="outline" className="h-8 border-zinc-300"
                          onClick={() => window.open("/api/payslips/" + p.id + "/pdf", "_blank")}>
                    Download
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {!rows.length && (
              <TableRow><TableCell colSpan={3} className="text-center text-sm text-zinc-500 py-8">
                Nothing here yet. An employee only sees a payslip once that month is released.
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function SelfView({ employeeId }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    let alive = true;
    setData(null); setErr("");
    api.get(`/employees/${employeeId}/self-view`)
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => { if (alive) setErr(formatApiError(e)); });
    return () => { alive = false; };
  }, [employeeId]);

  if (err) return <div className="text-sm text-red-600">{err}</div>;
  if (!data) return <div className="text-sm text-zinc-500">Loading…</div>;
  const emp = data.employee || {};
  const pending = data.pending_change;
  const probation = data.probation || {};

  return (
    <div className="space-y-4" data-testid="emp-self-view">
      <div className="text-xs text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2">
        This is {emp.name || "the employee"}'s own page, exactly as they see it. Read-only here —
        use Admin view to make changes.
      </div>

      {probation && probation.state && probation.state !== "unknown" && (
        <div className="text-sm text-zinc-700">
          Probation · {probation.months} month{probation.months === 1 ? "" : "s"}
          {probation.state ? ` · ${probation.state}` : ""}
        </div>
      )}
      {pending && (
        <Badge variant="outline" className="text-[11px] bg-amber-50 text-amber-700 border-amber-200">
          Changes pending review
        </Badge>
      )}

      <Tabs defaultValue="details">
        <TabsList>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="docs">Documents</TabsTrigger>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
          <TabsTrigger value="payslips">Payslips</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="mt-4">
          <Card className="border-zinc-200 shadow-none">
            <CardContent className="grid grid-cols-2 gap-4 p-4">
              <Field label="Name" value={emp.name} />
              <Field label="Employee code" value={emp.employee_code} />
              <Field label="PAN" value={emp.pan} />
              <Field label="Aadhaar" value={emp.aadhaar} />
              <Field label="Bank name" value={emp.bank_name} />
              <Field label="IFSC" value={emp.bank_ifsc} />
              <Field label="Bank account" value={emp.bank_account} />
              <Field label="Account holder" value={emp.account_holder_name} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="docs" className="mt-4">
          <DocumentsTab employeeId={employeeId} isAdmin={false} asEmployee />
        </TabsContent>

        <TabsContent value="timeline" className="mt-4">
          <TimelineTab employeeId={employeeId} isAdmin={false} />
        </TabsContent>

        <TabsContent value="payslips" className="mt-4">
          <SelfPayslips employeeId={employeeId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default function EmployeeDetailPage() {
  const { id } = useParams();
  // "admin" is the view that already existed; "employee" shows the same
  // page the employee sees when they sign in.
  const [mode, setMode] = useState("admin");
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [emp, setEmp] = useState(null);
  const [managerName, setManagerName] = useState("");
  const [rows, setRows] = useState([]);
  const [balances, setBalances] = useState([]);

  // Admin correction for a leave balance the policy engine got wrong.
  const editBalance = async (b) => {
    const label = b.leave_type?.name || b.leave_type?.code || "Leave";
    const alloc = window.prompt(`${label} — days allocated`, String(b.allocated ?? 0));
    if (alloc === null) return;
    const used = window.prompt(`${label} — days used`, String(b.used ?? 0));
    if (used === null) return;
    const reason = window.prompt("Why is this being corrected? It is stored with the change.");
    if (!reason) return;
    try {
      const { data } = await api.patch(`/leaves/balances/${id}/${b.leave_type_id}`,
        { allocated: Number(alloc), used: Number(used), reason });
      toast.success(`${label}: ${data.now.allocated} allocated, ${data.now.used} used`);
      const { data: fresh } = await api.get(`/leaves/balances/${id}`);
      setBalances(fresh || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update the balance");
    }
  };
  const [leaves, setLeaves] = useState([]);
  const [regs, setRegs] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);

  // Closing a tenure. Nothing is deleted: documents, payslips, attendance and
  // leave records all stay. Status is what removes them from payroll, the
  // daily attendance sheet and the working employee list.
  const [exiting, setExiting] = useState(false);
  const [adjFor, setAdjFor] = useState("");
  const [adjKind, setAdjKind] = useState("reimbursement");
  const [adjLabel, setAdjLabel] = useState("");
  const [adjAmount, setAdjAmount] = useState("");
  const [adjBusy, setAdjBusy] = useState(false);
  const [adjErr, setAdjErr] = useState("");

  const adjMsg = (e) => e?.response?.data?.detail || "Something went wrong";

  const reloadPayslips = async () => {
    try {
      const { data } = await api.get("/payslips", { params: { employee_id: id } });
      setPayslips(data || []);
    } catch (e) { /* leave the list as it was */ }
  };

  const addAdj = async (pid) => {
    setAdjErr("");
    setAdjBusy(true);
    try {
      await api.post(`/payslips/${pid}/adjustments`,
        { kind: adjKind, label: adjLabel, amount: adjAmount });
      setAdjLabel("");
      setAdjAmount("");
      await reloadPayslips();
    } catch (e) { setAdjErr(adjMsg(e)); }
    finally { setAdjBusy(false); }
  };

  const attachDoc = async (pid, aid, file) => {
    if (!file) return;
    setAdjErr("");
    try {
      // Reuse the employee document folder rather than inventing a second
      // place for files: the bill belongs with their other paperwork.
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", "reimbursement");
      fd.append("is_private", "false");
      fd.append("note", "Bill attached to a payslip adjustment");
      const { data } = await api.post(`/employees/${id}/documents`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      await api.patch(`/payslips/${pid}/adjustments/${aid}`,
        { document_id: data.id, document_name: data.filename || file.name });
      await reloadPayslips();
    } catch (e) { setAdjErr(adjMsg(e)); }
  };

  const removeAdj = async (pid, aid) => {
    if (!window.confirm("Remove this line from the payslip?")) return;
    setAdjErr("");
    try {
      await api.delete(`/payslips/${pid}/adjustments/${aid}`);
      await reloadPayslips();
    } catch (e) { setAdjErr(adjMsg(e)); }
  };

  const doExit = async () => {
    const today = new Date().toISOString().slice(0, 10);
    const day = window.prompt(
      "Last working day for " + emp.name + " (YYYY-MM-DD)", today);
    if (!day) return;
    const reason = window.prompt(
      "Reason for leaving (kept on their record and shown on the timeline)", "");
    if (reason === null) return;
    const ok = window.confirm(
      emp.name + " will be marked as exited on " + day.trim() + ".\n\n" +
      "They will come off payroll, the daily attendance sheet and the " +
      "employee list, and will no longer be able to sign in.\n\n" +
      "Nothing is deleted - their documents, payslips and history stay, " +
      "and you can still add an experience letter afterwards.");
    if (!ok) return;
    setExiting(true);
    try {
      await api.post(`/employees/${id}/exit`, {
        last_working_day: day.trim(), reason: reason.trim(),
      });
      toast.success(emp.name + " has been exited");
      // The page pulls the record, attendance, leave and pay in one effect on
      // mount, and there is no single refresh to call, so reload the view.
      window.location.reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setExiting(false); }
  };

  // Re-pull the attendance range after a correction. The rows load from an
  // inline effect keyed on the date range, so there is no loader to call.
  const refreshAttendance = async () => {
    try {
      const { data } = await api.get("/attendance/team", { params: { from_date: from, to_date: to } });
      const mine = (data?.employees || []).find((x) => x.employee.id === id);
      setRows(mine?.rows || []);
    } catch (e) { /* range may be mid-edit */ }
  };

  // Correct a day outright. Every change keeps the employee's original values
  // in an audit trail, so a corrected day can always be traced back.
  const editDay = async (r) => {
    const ci = window.prompt(`Log in for ${r.date} (HH:MM, blank to clear)`, (r.check_in || "").slice(11, 16));
    if (ci === null) return;
    const co = window.prompt(`Log out for ${r.date} (HH:MM, blank to clear)`, (r.check_out || "").slice(11, 16));
    if (co === null) return;
    const remarks = window.prompt("Remarks", r.remarks || "");
    if (remarks === null) return;
    const reason = window.prompt("Why is this being corrected? (optional)") || undefined;
    try {
      await api.patch(`/attendance/${r.id}`, { check_in: ci, check_out: co, remarks, reason });
      toast.success("Day updated");
      refreshAttendance();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Hand the day back instead of guessing on the employee's behalf - the
  // correction then comes in their own words.
  // Remove a day's attendance outright. Used when a record was logged in
  // error — a duplicate, a wrong employee, a test row.
  const cancelDay = async (r) => {
    const ok = window.confirm(
      `Remove the attendance record for ${r.date}?\n\nThis deletes the day entirely, including hours and task log. It cannot be undone.`);
    if (!ok) return;
    try {
      await api.delete(`/attendance/${r.id}`);
      toast.success(`Attendance for ${r.date} removed`);
      refreshAttendance();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove that day");
    }
  };

  const sendBack = async (r) => {
    const note = window.prompt(`What needs correcting on ${r.date}?\n\nThe employee sees this note and refills the day.`);
    if (!note) return;
    try {
      await api.post(`/attendance/${r.id}/request-correction`, { note });
      toast.success("Sent back to the employee");
      refreshAttendance();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const today = new Date();
  const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const [from, setFrom] = useState(ymd(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [to, setTo] = useState(ymd(today));

  useEffect(() => {
    (async () => {
      try {
        // status: all - the list endpoint now defaults to current staff, but this
      // page must still open the record of someone who has left.
      const { data: list } = await api.get("/employees", { params: { status: "all" } });
        const found = (list || []).find((e) => e.id === id);
        setEmp(found || null);
        if (found?.manager_id) {
          // the server resolves this now; the local lookup is only a fallback
          // for managers who cannot see their own manager's row
          const mgr = (list || []).find((e) => e.user_id === found.manager_id || e.id === found.manager_id);
          setManagerName(found.manager_name || (mgr ? mgr.name : ""));
        }
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [id]);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const { data } = await api.get("/attendance/team", { params: { from_date: from, to_date: to } });
        const mine = (data?.employees || []).find((x) => x.employee.id === id);
        setRows(mine?.rows || []);
      } catch (e) { /* range may be invalid mid-typing */ }
    })();
  }, [id, from, to]);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try { const { data } = await api.get(`/leaves/balances/${id}`); setBalances(data || []); } catch (e) {}
      try {
        const { data } = await api.get("/leaves/all");
        setLeaves((data || []).filter((l) => l.employee_id === id));
      } catch (e) {}
      try {
        const { data } = await api.get("/attendance/regularize");
        setRegs((data || []).filter((r) => r.employee_id === id));
      } catch (e) {}
      try {
        const { data } = await api.get("/payslips", { params: { employee_id: id } });
        setPayslips(data || []);
      } catch (e) {}
    })();
  }, [id]);

  if (loading) return <div className="text-sm text-zinc-500">Loading…</div>;
  if (!emp) return (
    <div className="space-y-4">
      <Button variant="ghost" onClick={() => navigate("/employees")} className="h-8"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
      <p className="text-sm text-zinc-500">Employee not found.</p>
    </div>
  );

  const present = rows.filter((r) => r.status === "present").length;
  const absent = rows.filter((r) => r.status === "absent").length;
  const half = rows.filter((r) => r.status === "half_day").length;
  const hours = rows.reduce((a, r) => a + (r.work_hours || 0), 0);
  const sal = emp.salary || {};

  return (
    <div className="space-y-6">
      <Button variant="ghost" onClick={() => navigate("/employees")} className="h-8 -ml-2" data-testid="back-to-employees">
        <ArrowLeft className="h-4 w-4 mr-1" /> Employees
      </Button>

      <div className="flex items-start gap-4 flex-wrap">
        <div className="h-14 w-14 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center text-lg font-semibold">{initials(emp.name)}</div>
        <div className="flex-1 min-w-0">
          <h1 className="font-display text-3xl font-bold tracking-tight text-zinc-900">{emp.name}</h1>
          <p className="mt-1 text-sm text-zinc-500">
            {emp.employee_code} · {emp.designation || "—"} · {emp.department || "—"}
          </p>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <Badge className={emp.status === "active" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-zinc-100 text-zinc-700 border border-zinc-200"}>{emp.status}</Badge>
            <Badge variant="outline" className="capitalize text-[11px]">{emp.employment_type === "intern" ? "Intern" : "Full-time"}</Badge>
            <span className="text-xs text-zinc-500">Joined {fmtDate(emp.join_date)}</span>
                {emp.last_working_day && (
                  <span className="text-xs text-amber-700">
                    Exited {fmtDate(emp.last_working_day)}
                    {emp.exit_reason ? " · " + emp.exit_reason : ""}
                  </span>
                )}
                {isAdmin && emp.status === "active" && (
                  <Button size="sm" variant="outline" disabled={exiting}
                    onClick={doExit} data-testid="exit-employee"
                    className="h-8 text-xs border-amber-300 text-amber-800 hover:bg-amber-50">
                    {exiting ? "Working..." : "Exit employee"}
                  </Button>
                )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[["Present", present], ["Absent", absent], ["Half day", half], ["Hours", Math.round(hours * 10) / 10]].map(([k, v]) => (
          <Card key={k} className="border-zinc-200 shadow-none"><CardContent className="p-4">
            <div className="text-xs text-zinc-500">{k}</div>
            <div className="text-2xl font-semibold text-zinc-900 mt-1">{v}</div>
          </CardContent></Card>
        ))}
      </div>

        <div className="flex items-center gap-2 mb-4" data-testid="emp-view-toggle">
          <Button size="sm" onClick={() => setMode("admin")} data-testid="emp-view-admin"
                  className={mode === "admin" ? "h-8 bg-zinc-900 hover:bg-zinc-800" : "h-8 bg-white text-zinc-700 border border-zinc-300 hover:bg-zinc-50"}>
            Admin view
          </Button>
          <Button size="sm" onClick={() => setMode("employee")} data-testid="emp-view-employee"
                  className={mode === "employee" ? "h-8 bg-zinc-900 hover:bg-zinc-800" : "h-8 bg-white text-zinc-700 border border-zinc-300 hover:bg-zinc-50"}>
            Employee view
          </Button>
        </div>

        {mode === "admin" ? (
      <Tabs defaultValue="profile">
        <TabsList>
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="attendance">Attendance</TabsTrigger>
          <TabsTrigger value="leave">Leave</TabsTrigger>
          <TabsTrigger value="regs">Regularizations</TabsTrigger>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
          {isAdmin && <TabsTrigger value="docs">Documents</TabsTrigger>}
          {isAdmin && <TabsTrigger value="pay">Pay</TabsTrigger>}
        </TabsList>

        <TabsContent value="profile" className="mt-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Card className="border-zinc-200 shadow-none"><CardContent className="p-5">
              <h3 className="text-sm font-medium text-zinc-900 mb-2">Personal</h3>
              <Field label="Work email" value={emp.email} />
            <Field label="Last signed in" value={since(emp.last_seen_at)} />
              <Field label="Personal email" value={emp.personal_email} />
              <Field label="Phone" value={emp.phone} />
              <Field label="Date of birth" value={emp.dob ? fmtDate(emp.dob) : ""} />
              <Field label="Address" value={emp.address} />
              <Field label="Emergency contact" value={emp.emergency_contact_name ? `${emp.emergency_contact_name} · ${emp.emergency_contact_phone || ""}` : ""} />
            </CardContent></Card>

            <Card className="border-zinc-200 shadow-none"><CardContent className="p-5">
              <h3 className="text-sm font-medium text-zinc-900 mb-2">Employment</h3>
              <Field label="Employee code" value={emp.employee_code} />
              <Field label="Designation" value={emp.designation} />
              <Field label="Department" value={emp.department} />
              <Field label="Manager" value={managerName} />
              <Field label="Join date" value={fmtDate(emp.join_date)} />
              <Field label="Type" value={emp.employment_type === "intern" ? "Intern" : "Full-time"} />
            </CardContent></Card>

            {isAdmin && (
              <Card className="border-zinc-200 shadow-none"><CardContent className="p-5">
                <h3 className="text-sm font-medium text-zinc-900 mb-2">Identity</h3>
                <Field label="PAN" value={emp.pan} />
                <Field label="Aadhaar" value={emp.aadhaar} />
              </CardContent></Card>
            )}

            {isAdmin && (
              <Card className="border-zinc-200 shadow-none"><CardContent className="p-5">
                <h3 className="text-sm font-medium text-zinc-900 mb-2">Banking</h3>
                <Field label="Account holder" value={emp.account_holder_name} />
                <Field label="Bank" value={emp.bank_name} />
                <Field label="Account number" value={emp.bank_account} />
                <Field label="IFSC" value={emp.bank_ifsc} />
              </CardContent></Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="attendance" className="mt-4 space-y-3">
          <div className="flex items-end gap-3 flex-wrap">
            <div><div className="text-xs text-zinc-500 mb-1">From</div>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 rounded-md border border-zinc-300 px-2 text-sm" /></div>
            <div><div className="text-xs text-zinc-500 mb-1">To</div>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 rounded-md border border-zinc-300 px-2 text-sm" /></div>
          </div>
          <Card className="border-zinc-200 shadow-none"><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent">
                <TableHead>Date</TableHead><TableHead>Log in</TableHead><TableHead>Log out</TableHead>
                <TableHead>Hours</TableHead><TableHead>Status</TableHead><TableHead>Task given</TableHead><TableHead>Remarks</TableHead>{isAdmin && <TableHead className="text-right">Actions</TableHead>}
              </TableRow></TableHeader>
              <TableBody>
                {rows.filter((r) => r.check_in || r.tasks_given || r.remarks || r.status !== "absent").map((r) => (
                  <TableRow key={r.id} className="border-b border-zinc-100">
                    <TableCell className="text-sm whitespace-nowrap">{fmtDate(r.date)}</TableCell>
                    <TableCell className="text-sm">{fmtTime(r.check_in, r.is_regularized)}</TableCell>
                    <TableCell className="text-sm">{fmtTime(r.check_out, r.is_regularized)}</TableCell>
                    <TableCell className="text-sm tabular-nums">{r.work_hours || 0}</TableCell>
                    <TableCell><Badge variant="outline" className="text-[11px] capitalize">{String(r.status || "").replace("_", " ")}</Badge></TableCell>
                    <TableCell className="text-sm text-zinc-600 max-w-[240px] whitespace-pre-wrap">{r.tasks_given || "—"}</TableCell>
                    <TableCell className="text-sm text-zinc-500">{r.remarks || "—"}</TableCell>
                    {isAdmin && (
                      <TableCell className="text-right whitespace-nowrap align-top">
                        <button className="text-xs text-zinc-500 hover:text-zinc-900 hover:underline" data-testid={`att-edit-${r.id}`} onClick={() => editDay(r)}>Edit</button>
                        <span className="text-zinc-300 mx-2">|</span>
                        <button className="text-xs text-zinc-500 hover:text-amber-700 hover:underline" data-testid={`att-sendback-${r.id}`} onClick={() => sendBack(r)}>Send back</button>
                        <span className="text-zinc-300 mx-1">|</span>
                        <button className="text-xs text-red-600 hover:text-red-700 hover:underline"
                          data-testid={`att-cancel-${r.id}`} onClick={() => cancelDay(r)}>Cancel</button>
                        {r.needs_correction && <div className="text-[11px] text-amber-700 mt-1 max-w-[190px] whitespace-normal text-left">Awaiting employee: {r.correction_note}</div>}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {!rows.length && <TableRow><TableCell colSpan={isAdmin ? 8 : 7} className="text-center text-sm text-zinc-500 py-10">No attendance in this range.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="leave" className="mt-4 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {balances.map((b) => (
              <Card key={b.id || b.leave_type_id} className="border-zinc-200 shadow-none"><CardContent className="p-4">
                <div className="text-xs text-zinc-500">{b.leave_type?.name || "Leave"}</div>
                <div className="text-2xl font-semibold text-zinc-900 mt-1">{b.balance ?? ((b.allocated ?? 0) - (b.used ?? 0))}</div>
                <div className="text-xs text-zinc-500 mt-1">{b.used ?? 0} used of {b.allocated ?? 0}</div>
                {b.manual_override && <div className="text-[10px] text-amber-700 mt-1">Adjusted by admin</div>}
                {isAdmin && (
                  <button type="button" onClick={() => editBalance(b)}
                    className="mt-2 text-xs text-emerald-700 hover:underline" data-testid="edit-balance-btn">Edit</button>
                )}
              </CardContent></Card>
            ))}
            {!balances.length && <p className="text-sm text-zinc-500">No leave balances recorded.</p>}
          </div>
          <Card className="border-zinc-200 shadow-none"><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent">
                <TableHead>From</TableHead><TableHead>To</TableHead><TableHead>Days</TableHead><TableHead>Reason</TableHead><TableHead>Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {leaves.map((l) => (
                  <TableRow key={l.id} className="border-b border-zinc-100">
                    <TableCell className="text-sm">{fmtDate(l.from_date)}</TableCell>
                    <TableCell className="text-sm">{fmtDate(l.to_date)}</TableCell>
                    <TableCell className="text-sm tabular-nums">{l.days ?? "—"}</TableCell>
                    <TableCell className="text-sm text-zinc-600">{l.reason || "—"}</TableCell>
                    <TableCell><Badge variant="outline" className="text-[11px] capitalize">{l.status}</Badge></TableCell>
                  </TableRow>
                ))}
                {!leaves.length && <TableRow><TableCell colSpan={5} className="text-center text-sm text-zinc-500 py-10">No leave requests.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="regs" className="mt-4">
          <Card className="border-zinc-200 shadow-none"><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent">
                <TableHead>Date</TableHead><TableHead>Reason</TableHead><TableHead>Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {regs.map((r) => (
                  <TableRow key={r.id} className="border-b border-zinc-100">
                    <TableCell className="text-sm">{fmtDate(r.date)}</TableCell>
                    <TableCell className="text-sm text-zinc-600">{r.reason || "—"}</TableCell>
                    <TableCell><Badge variant="outline" className="text-[11px] capitalize">{r.status}</Badge></TableCell>
                  </TableRow>
                ))}
                {!regs.length && <TableRow><TableCell colSpan={3} className="text-center text-sm text-zinc-500 py-10">No regularizations.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="timeline" className="mt-4">
          <TimelineTab employeeId={id} isAdmin={isAdmin} />
        </TabsContent>

        <TabsContent value="docs" className="mt-4">
          <DocumentsTab employeeId={id} isAdmin={isAdmin} />
        </TabsContent>

        {isAdmin && (
          <TabsContent value="pay" className="mt-4 space-y-4">
            <Card className="border-zinc-200 shadow-none"><CardContent className="p-5">
              <h3 className="text-sm font-medium text-zinc-900 mb-2">Salary structure</h3>
              <div className="grid md:grid-cols-2 gap-x-6">
                <Field label="CTC (annual)" value={sal.ctc_annual ? `₹ ${Number(sal.ctc_annual).toLocaleString("en-IN")}` : ""} />
                <Field label="Basic" value={sal.basic ? `₹ ${Number(sal.basic).toLocaleString("en-IN")}` : ""} />
                <Field label="HRA" value={sal.hra ? `₹ ${Number(sal.hra).toLocaleString("en-IN")}` : ""} />
                <Field label="Special allowance" value={sal.special_allowance ? `₹ ${Number(sal.special_allowance).toLocaleString("en-IN")}` : ""} />
                <Field label="Monthly stipend" value={sal.monthly_stipend ? `₹ ${Number(sal.monthly_stipend).toLocaleString("en-IN")}` : ""} />
                <Field label="Deductions" value={sal.deductions_manual ? `₹ ${Number(sal.deductions_manual).toLocaleString("en-IN")}` : ""} />
              </div>
            </CardContent></Card>
            <Card className="border-zinc-200 shadow-none"><CardContent className="p-0">
              {payslips.length ? (
                <div className="flex justify-end pb-3">
                  <Button variant="outline" size="sm" data-testid="payslips-all"
                          onClick={() => window.open(`/api/employees/${id}/payslips/combined`, "_blank")}>
                    Download all payslips
                  </Button>
                </div>
              ) : null}
              <Table>
                <TableHeader><TableRow className="hover:bg-transparent">
                  <TableHead>Month</TableHead><TableHead>Net pay</TableHead><TableHead className="text-right">PDF</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {payslips.map((p) => (
                    <TableRow key={p.id} className="border-b border-zinc-100">
                      <TableCell className="text-sm">{p.month || "—"} {p.year || ""}</TableCell>
                      <TableCell className="text-sm tabular-nums">{p.net_pay ? `₹ ${Number(p.net_pay).toLocaleString("en-IN")}` : "—"}</TableCell>
                      <TableCell className="text-right">
                        <button className="text-xs text-zinc-600 hover:underline mr-3"
                                onClick={() => { setAdjFor(adjFor === p.id ? "" : p.id); setAdjErr(""); }}
                                data-testid={`ps-adjust-${p.id}`}>
                          {adjFor === p.id ? "Close" : "Adjust"}
                        </button>
                        <Button size="sm" variant="ghost" className="h-8" onClick={() => window.open(`/api/payslips/${p.id}/pdf`, "_blank")}>
                          <Download className="h-4 w-4" />
                        </Button>
                      {adjFor === p.id ? (
                        <div className="mt-2 text-left border border-zinc-200 rounded-md p-3 space-y-2 bg-zinc-50"
                             data-testid="ps-adjust-panel">
                          {(p.adjustments || []).length ? (p.adjustments || []).map((a) => (
                            <div key={a.id} className="flex items-center justify-between gap-3 text-xs">
                              <span className="truncate">
                                <span className="text-zinc-500">{a.kind === "reimbursement" ? "Reimbursement" : "Extra earning"}:</span> {a.label}
                              </span>
                              <span className="flex items-center gap-3 shrink-0">
                                <span className="tabular-nums">{Number(a.amount || 0).toLocaleString("en-IN")}</span>
                                {a.document_id ? (
                                  <a className="text-emerald-700 hover:underline"
                                     href={`/api/employees/${id}/documents/${a.document_id}/download`}
                                     target="_blank" rel="noreferrer">{a.document_name || "Bill"}</a>
                                ) : (
                                  <label className="text-zinc-500 hover:underline cursor-pointer">
                                    Attach bill
                                    <input type="file" className="hidden"
                                           onChange={(e) => attachDoc(p.id, a.id, e.target.files?.[0])} />
                                  </label>
                                )}
                                <button className="text-red-600 hover:underline" onClick={() => removeAdj(p.id, a.id)}>Remove</button>
                              </span>
                            </div>
                          )) : <div className="text-xs text-zinc-400">No adjustments on this payslip.</div>}
                      
                          <div className="flex flex-wrap items-center gap-2 pt-1">
                            <select className="border border-zinc-300 rounded px-2 py-1 text-xs bg-white"
                                    value={adjKind} onChange={(e) => setAdjKind(e.target.value)}>
                              <option value="reimbursement">Reimbursement (not taxable)</option>
                              <option value="earning">Extra earning (taxable)</option>
                            </select>
                            <input className="border border-zinc-300 rounded px-2 py-1 text-xs flex-1 min-w-[150px]"
                                   placeholder="Description" value={adjLabel} onChange={(e) => setAdjLabel(e.target.value)} />
                            <input className="border border-zinc-300 rounded px-2 py-1 text-xs w-24"
                                   placeholder="Amount" value={adjAmount} onChange={(e) => setAdjAmount(e.target.value)} />
                            <Button size="sm" className="h-7 text-xs" disabled={adjBusy} onClick={() => addAdj(p.id)}>
                              {adjBusy ? "Saving..." : "Add"}
                            </Button>
                          </div>
                      
                          {adjErr ? <div className="text-xs text-red-600">{adjErr}</div> : null}
                      
                          <div className="text-xs text-zinc-500 pt-1 border-t border-zinc-200">
                            Net {Number(p.net_pay || 0).toLocaleString("en-IN")}
                            {" + reimbursements "}{Number(p.reimbursements || 0).toLocaleString("en-IN")}
                            {" = payable "}<b>{Number(p.total_payment ?? p.net_pay ?? 0).toLocaleString("en-IN")}</b>
                          </div>
                        </div>
                      ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                  {!payslips.length && <TableRow><TableCell colSpan={3} className="text-center text-sm text-zinc-500 py-10">No payslips yet.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent></Card>
          </TabsContent>
        )}
      </Tabs>
        ) : (
          <SelfView employeeId={id} />
        )}
    </div>
  );
}
